import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const API_URL = window.__BACKEND_URL__;

/**
 * SavedContext — single source of truth for saved/favourited items.
 *
 * The saved set is keyed by "<item_type>:<item_id>" so the same context can
 * serve every entity type (talent, casting_call, service, agent,
 * casting_professional, company, etc.) without collisions.
 *
 * All save/unsave mutations go through here so every heart/save button
 * rendered anywhere in the app stays in sync via React re-renders. No
 * duplicate local `savedItems` sets, no stale state when navigating
 * detail → back.
 */

const SavedContext = createContext(null);

const keyOf = (type, id) => `${type}:${id}`;

export function SavedProvider({ children }) {
  const { user } = useAuth();
  const [savedSet, setSavedSet] = useState(() => new Set());
  const [loaded, setLoaded] = useState(false);
  const loadingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setSavedSet(new Set());
      setLoaded(true);
      return;
    }
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const { data } = await axios.get(`${API_URL}/api/saved`, { withCredentials: true });
      const next = new Set();
      (data || []).forEach((row) => {
        if (row && row.item_type && row.item_id) {
          next.add(keyOf(row.item_type, row.item_id));
        }
      });
      setSavedSet(next);
    } catch (err) {
      // Silent — saved state simply stays at whatever we had; individual
      // pages can still call toggle which reconciles with the backend.
      console.error('Failed to load saved items:', err);
    } finally {
      loadingRef.current = false;
      setLoaded(true);
    }
  }, [user]);

  // Refresh whenever the auth user changes (login/logout/switch).
  useEffect(() => {
    refresh();
  }, [refresh]);

  const isSaved = useCallback(
    (type, id) => {
      if (!type || !id) return false;
      return savedSet.has(keyOf(type, id));
    },
    [savedSet]
  );

  const setLocal = useCallback((type, id, saved) => {
    setSavedSet((prev) => {
      const k = keyOf(type, id);
      const had = prev.has(k);
      if (saved && had) return prev;
      if (!saved && !had) return prev;
      const next = new Set(prev);
      if (saved) next.add(k);
      else next.delete(k);
      return next;
    });
  }, []);

  const toggleSaved = useCallback(
    async (type, id, options = {}) => {
      if (!type || !id) return null;
      if (!user) {
        toast.error('Please sign in to save items');
        return null;
      }

      const currentlySaved = savedSet.has(keyOf(type, id));
      const nextSaved = !currentlySaved;

      // Optimistic UI update so every heart flips instantly.
      setLocal(type, id, nextSaved);

      try {
        if (currentlySaved) {
          await axios.delete(`${API_URL}/api/saved/${type}/${id}`, {
            withCredentials: true,
          });
        } else {
          await axios.post(
            `${API_URL}/api/saved`,
            { item_type: type, item_id: id },
            { withCredentials: true }
          );
        }
        if (options.silent !== true) {
          toast.success(nextSaved ? 'Saved to favourites' : 'Removed from saved');
        }
        return nextSaved;
      } catch (err) {
        const status = err?.response?.status;
        // Idempotent handling: treat "already saved" / "not found" as the
        // actual backend state and leave the optimistic flip in place so
        // the UI reflects reality (fixes duplicate-save & stale-card bugs).
        if (!currentlySaved && status === 400) {
          // Was actually saved on the server; keep local=saved.
          if (options.silent !== true) toast.success('Already saved');
          return true;
        }
        if (currentlySaved && status === 404) {
          // Was already unsaved on the server; keep local=unsaved.
          if (options.silent !== true) toast.success('Removed from saved');
          return false;
        }
        // Real failure — roll back optimistic update.
        setLocal(type, id, currentlySaved);
        toast.error('Failed to update saved items');
        return currentlySaved;
      }
    },
    [savedSet, setLocal, user]
  );

  const value = useMemo(
    () => ({ savedSet, isSaved, toggleSaved, refresh, setLocal, loaded }),
    [savedSet, isSaved, toggleSaved, refresh, setLocal, loaded]
  );

  return <SavedContext.Provider value={value}>{children}</SavedContext.Provider>;
}

export function useSaved() {
  const ctx = useContext(SavedContext);
  if (!ctx) {
    throw new Error('useSaved must be used within a SavedProvider');
  }
  return ctx;
}
